import { ChevronArrowRightIcon, DraggableItemIcon, NavCloseIcon } from 'assets/icons';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';

export interface Tab {
  id: string;
  label: string;
  count?: number;
  showClose?: boolean;
}

interface DraggableTabListProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onReorder?: (newOrder: Tab[], oldOrder: Tab[]) => void;
  className?: string;
  onRemoveTab?: (tabId: string) => void;
  tabClassName?: string;
  disabled?: boolean;
}

export const DraggableTabList = ({
  tabs,
  activeTab,
  onTabChange,
  onReorder,
  className,
  onRemoveTab,
  tabClassName,
  disabled = false
}: DraggableTabListProps) => {
  const [orderedTabs, setOrderedTabs] = useState<Tab[]>(tabs);
  const [draggedTab, setDraggedTab] = useState<number | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  useEffect(() => {
    setOrderedTabs(tabs);
  }, [tabs]);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, orderedTabs.length);
  }, [orderedTabs]);

  const checkScrollButtons = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollButtons);
      }
    };
  }, [orderedTabs]);

  const scroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = containerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      containerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleDragStart = (index: number) => {
    if (disabled) return;
    setDraggedTab(index);
  };

  const handleDragStop = (index: number) => {
    if (draggedTab === null) return;

    const draggedRect = tabRefs.current[draggedTab]?.getBoundingClientRect();
    if (!draggedRect) return;

    const draggedCenter = draggedRect.left + draggedRect.width / 2;

    let newIndex = index;
    tabRefs.current.forEach((ref, i) => {
      if (!ref || i === draggedTab) return;
      const rect = ref.getBoundingClientRect();
      if (draggedCenter > rect.left && draggedCenter < rect.right) {
        newIndex = i;
      }
    });

    if (newIndex !== draggedTab) {
      const newTabs = [...orderedTabs];
      const [removed] = newTabs.splice(draggedTab, 1);
      newTabs.splice(newIndex, 0, removed);
      setOrderedTabs(newTabs);
      onReorder?.(newTabs, orderedTabs);
    }

    setDraggedTab(null);
  };

  const handleTabClick = (tabId: string) => {
    if (disabled || activeTab === tabId) {
      return;
    }
    onTabChange(tabId);
  };

  const handleRemoveTab = (event: React.MouseEvent<HTMLDivElement>, tabId: string) => {
    event.stopPropagation();
    if (onRemoveTab) {
      onRemoveTab(tabId);
    }
  };

  useEffect(() => {
    const activeTabElement = tabRefs.current.find((_, index) => orderedTabs[index]?.id === activeTab);
    if (activeTabElement && containerRef.current) {
      const container = containerRef.current;
      const tabRect = activeTabElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollLeft = tabRect.left - containerRect.left - (containerRect.width - tabRect.width) / 2;

      container.scrollTo({
        left: container.scrollLeft + scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeTab, orderedTabs]);

  return (
    <div className={classNames('relative flex items-center', { 'pointer-events-none opacity-60': disabled })}>
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 z-10 h-full border border-borderGray bg-primary px-2 shadow"
          aria-label="Scroll left">
          <ChevronArrowRightIcon className="rotate-180 " />
        </button>
      )}
      <div
        ref={containerRef}
        className={twMerge('flex w-full items-center overflow-x-hidden scroll-smooth', className)}>
        {orderedTabs.map((tab, index) => (
          <Draggable
            key={tab.id}
            axis="x"
            position={{ x: 0, y: 0 }}
            onStart={() => handleDragStart(index)}
            onStop={() => handleDragStop(index)}>
            <div
              onClick={() => handleTabClick(tab.id)}
              ref={el => (tabRefs.current[index] = el)}
              className={twMerge(
                `group relative cursor-grab touch-none select-none flex bg-secondaryBtn
                items-center min-w-[144px] max-w-[228px] border-r border-borderGray border-l-0 px-3
              [&.active]:border-b-primary [&.active]:bg-primary shrink-0`,
                draggedTab === index && 'z-10',
                activeTab === tab.id && 'active bg-primary',

                tabClassName
              )}>
              <DraggableItemIcon className="invisible shrink-0 group-hover:visible" />
              <div
                onClick={() => handleTabClick(tab.id)}
                className="mx-auto w-full cursor-pointer overflow-hidden truncate px-3.5
                  py-1.5 text-center
                  text-sbase font-semibold text-tertiaryText duration-200"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                tabIndex={0}
                title={tab.label}>
                {tab.label}
              </div>

              {tab.showClose && (
                <div
                  onClick={event => handleRemoveTab(event, tab.id)}
                  className={classNames('cursor-pointer p-2 group-hover:visible', {
                    visible: activeTab === tab.id,
                    invisible: activeTab !== tab.id
                  })}>
                  <NavCloseIcon className=" h-3 w-3 shrink-0 " />
                </div>
              )}
            </div>
          </Draggable>
        ))}
      </div>
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 z-10 h-full border border-borderGray bg-primary px-2 shadow"
          aria-label="Scroll right">
          <ChevronArrowRightIcon />
        </button>
      )}
    </div>
  );
};
